import type { ZeroviaClient } from "@agentry/api-client";
import { Route, Routes } from "react-router";
import { ApiProvider } from "./app/ApiProvider";
import { AuthProvider } from "./app/AuthProvider";
import { ProjectScopeGuard, RoleGuard } from "./app/RoleGuard";
import { enabledScreenModules, SCREEN_MODULES, type ScreenModule } from "./app/screen-module";
import { AppShell } from "./shell/AppShell";
import { PublicShell } from "./shell/PublicShell";
import { ThemeProvider } from "./shell/theme";
import { SiteRoot } from "./sites/SiteRoot";
import { AppErrorBoundary, NotFoundState } from "./ui/states";

function Screen({ module }: { module: ScreenModule }) {
	const Component = module.component;
	if (module.area === "public") {
		return (
			<PublicShell>
				<Component />
			</PublicShell>
		);
	}
	return (
		<RoleGuard area={module.area} roles={module.roles} requireScope={false}>
			<AppShell area={module.area} backing={module.backing}>
				<ProjectScopeGuard>
					<Component />
				</ProjectScopeGuard>
			</AppShell>
		</RoleGuard>
	);
}

export function AppRoutes({ modules = SCREEN_MODULES }: { modules?: readonly ScreenModule[] }) {
	return (
		<Routes>
			{enabledScreenModules(modules).map((module) => (
				<Route key={module.id} path={module.path} element={<Screen module={module} />} />
			))}
			<Route
				path="*"
				element={
					<PublicShell>
						<div className="mx-auto max-w-3xl px-4 py-16">
							<NotFoundState />
						</div>
					</PublicShell>
				}
			/>
		</Routes>
	);
}

export function App({
	client,
	modules,
}: {
	client?: ZeroviaClient;
	modules?: readonly ScreenModule[];
}) {
	return (
		<AppErrorBoundary>
			<ApiProvider client={client}>
				<AuthProvider>
					<ThemeProvider>
						<SiteRoot>
							<AppRoutes modules={modules} />
						</SiteRoot>
					</ThemeProvider>
				</AuthProvider>
			</ApiProvider>
		</AppErrorBoundary>
	);
}
